import { useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import { errorMessage, successMessage } from "../../../utils";
import { getAppSettings, updateAppSettings } from "../api";

export const useAppSettings = () => {
  const [, forceUpdate] = useState();
  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: () => forceUpdate(Date.now()) },
      validators: {
        ImageFormat: {
          message: "The Image should be png, jpg, jpeg",
          rule: (value) => {
            return (
              value === null ||
              ["image/png", "image/jpeg", "image/jpg"].includes(value.type)
            );
          },
        },
        ImageSize: {
          message: "The Image Size should be less than 1MB",
          rule: (value) => {
            return value === null || value.size <= 1024 * 1024;
          },
        },
      },
    })
  );

  const [state, setState] = useImmer({
    isLoading: true,
    isSaveButton: true,
    isSaveButtonBusy: false,
    logoFile: null,
    appIconFile: null,
    details: {
      name: "",
      logo: "",
      app_icon: "",
      address: "",
      phone_number: "",
      colors: {
        colorPrimary: "",
        colorPrimaryLight: "",
        colorSecondary: "",
        colorAccent: "",
      },
      review_configurable: 0,
      facebook_url: "",
      instagram_url: "",
      display_email: "",
      reply_to_address: "",
      config_mail: "",
      terms_url: "",
      privacy_policy_url: "",
      help_url: "",
      stripe_publishable_key: "",
      country_code: "",
      currency_code: "",
      banjos_vip_text_configurable: 0,
      loyalty_term: "",
      login_otp_configurable: 0,
      google_api_key: {
        ios: "",
        android: "",
        web: "",
      },
      firebase_data: {
        ios: {
          store_url: "",
          force_update_required: 0,
          force_update_current_version: "",
          store_version: "",
          message: "",
          title: "",
        },
        android: {
          store_url: "",
          force_update_required: 0,
          force_update_current_version: "",
          store_version: "",
          message: "",
          title: "",
        },
      },
    },
  });

  useEffect(() => {
    handleAppSettingsDetails();
  }, []);

  // API returns flags as strings ("0"/"1"), normalize them to 0/1
  const toFlag = (value) => (Number(value) ? 1 : 0);

  const handleAppSettingsDetails = async () => {
    try {
      const res = await getAppSettings();
      if (res.success) {
        const data = res.data;
        setState((draft) => {
          draft.details = {
            ...draft.details,
            ...data,
            colors: {
              ...draft.details.colors,
              ...(data.colors ?? {}),
            },
            google_api_key: {
              ...draft.details.google_api_key,
              ...(data.google_api_key ?? {}),
            },
            firebase_data: {
              ios: {
                ...draft.details.firebase_data.ios,
                ...(data.firebase_data?.ios ?? {}),
              },
              android: {
                ...draft.details.firebase_data.android,
                ...(data.firebase_data?.android ?? {}),
              },
            },
          };
          draft.details.review_configurable = toFlag(data.review_configurable);
          draft.details.banjos_vip_text_configurable = toFlag(
            data.banjos_vip_text_configurable
          );
          draft.details.login_otp_configurable = toFlag(
            data.login_otp_configurable
          );
          draft.details.firebase_data.ios.force_update_required = toFlag(
            data.firebase_data?.ios?.force_update_required
          );
          draft.details.firebase_data.android.force_update_required = toFlag(
            data.firebase_data?.android?.force_update_required
          );
          draft.isLoading = false;
        });
      }
    } catch (error) {
      errorMessage(error.response?.data?.message);
      setState((draft) => {
        draft.isLoading = false;
      });
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.details[name] = value;
      draft.isSaveButton = false;
    });
  };

  const handleSwitchChange = (event, name) => {
    const checked = event.target.checked;
    setState((draft) => {
      draft.details[name] = checked ? 1 : 0;
      draft.isSaveButton = false;
    });
  };

  const handleColorChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.details.colors[name] = value;
      draft.isSaveButton = false;
    });
  };

  const handleGoogleApiKeyChange = (event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.details.google_api_key[name] = value;
      draft.isSaveButton = false;
    });
  };

  const handleFirebaseChange = (platform, event) => {
    const { name, value } = event.target;
    setState((draft) => {
      draft.details.firebase_data[platform][name] = value;
      draft.isSaveButton = false;
    });
  };

  const handleFirebaseSwitchChange = (platform, event) => {
    const checked = event.target.checked;
    setState((draft) => {
      draft.details.firebase_data[platform].force_update_required = checked
        ? 1
        : 0;
      draft.isSaveButton = false;
    });
  };

  const onFileChange = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    setState((draft) => {
      if (type === "logo") {
        draft.logoFile = file;
      } else {
        draft.appIconFile = file;
      }
      draft.isSaveButton = false;
    });
    event.target.value = "";
  };

  const appendField = (formData, key, value) => {
    if (value === null || value === undefined) return;
    formData.append(key, value);
  };

  const onUpdateAppSettings = async () => {
    if (!formValidator.current.allValid()) {
      formValidator.current.showMessages();
      forceUpdate(Date.now());
      return;
    }
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });
      const { details } = state;
      const formData = new FormData();

      // App settings
      appendField(formData, "name", details.name);
      appendField(formData, "address", details.address);
      appendField(formData, "reply_to_address", details.reply_to_address);
      appendField(formData, "config_mail", details.config_mail);
      appendField(formData, "terms_url", details.terms_url);
      appendField(formData, "privacy_policy_url", details.privacy_policy_url);
      appendField(formData, "help_url", details.help_url);
      appendField(
        formData,
        "stripe_publishable_key",
        details.stripe_publishable_key
      );
      appendField(formData, "country_code", details.country_code);
      appendField(formData, "currency_code", details.currency_code);
      appendField(formData, "loyalty_term", details.loyalty_term);
      appendField(
        formData,
        "review_configurable",
        details.review_configurable ? 1 : 0
      );
      appendField(
        formData,
        "banjos_vip_text_configurable",
        details.banjos_vip_text_configurable ? 1 : 0
      );
      appendField(
        formData,
        "login_otp_configurable",
        details.login_otp_configurable ? 1 : 0
      );
      appendField(formData, "color_primary", details.colors.colorPrimary);
      appendField(
        formData,
        "color_primary_light",
        details.colors.colorPrimaryLight
      );
      appendField(formData, "color_secondary", details.colors.colorSecondary);
      appendField(formData, "color_accent", details.colors.colorAccent);
      appendField(formData, "google_api_key", details.google_api_key.web);
      appendField(
        formData,
        "ios_google_api_key",
        details.google_api_key.ios
      );
      appendField(
        formData,
        "android_google_api_key",
        details.google_api_key.android
      );

      // Contact settings
      appendField(formData, "phone_number", details.phone_number);
      appendField(formData, "display_email", details.display_email);
      appendField(formData, "facebook_url", details.facebook_url);
      appendField(formData, "instagram_url", details.instagram_url);

      // iOS and Android settings
      ["ios", "android"].forEach((platform) => {
        const data = details.firebase_data[platform];
        appendField(formData, `${platform}_store_url`, data.store_url);
        appendField(
          formData,
          `${platform}_force_update_required`,
          data.force_update_required ? 1 : 0
        );
        appendField(
          formData,
          `${platform}_force_update_current_version`,
          data.force_update_current_version
        );
        appendField(formData, `${platform}_store_version`, data.store_version);
        appendField(formData, `${platform}_message`, data.message);
        appendField(formData, `${platform}_title`, data.title);
      });

      state.logoFile && formData.append("logo", state.logoFile);
      state.appIconFile && formData.append("app_icon", state.appIconFile);

      const res = await updateAppSettings(formData);
      if (res.success) {
        successMessage(res.message);
        setState((draft) => {
          draft.isSaveButtonBusy = false;
          draft.isSaveButton = true;
          draft.logoFile = null;
          draft.appIconFile = null;
        });
        handleAppSettingsDetails();
      } else {
        errorMessage(res.message);
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(err.response?.data?.message);
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  return {
    state,
    formValidator,
    handleInputChange,
    handleSwitchChange,
    handleColorChange,
    handleGoogleApiKeyChange,
    handleFirebaseChange,
    handleFirebaseSwitchChange,
    onFileChange,
    onUpdateAppSettings,
  };
};
